import PropTypes from "prop-types"
import React, { Component } from "react"
import { UncontrolledTooltip } from "reactstrap"
import SimpleBar from "simplebar-react"
import { Link } from "react-router-dom"
import { connect } from "react-redux"
import { withTranslation } from "react-i18next"
import { ChevronDown, PanelLeftClose, PanelLeftOpen, X } from "lucide-react"

import { withRouter } from "../../components/Common/withRouter"
import logo from "../../assets/images/VeeperLogo.svg"
import { MENU_GROUPS, findActiveGroupKey, isMenuItemActive } from "./sidebarMenu"

// Matches the `max-width: 992px` breakpoint where the sidebar becomes a drawer
const MOBILE_BREAKPOINT = 992
const GROUPS_STORAGE_KEY = "vp-sidebar-closed-groups"

const readClosedGroups = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(GROUPS_STORAGE_KEY) || "[]")
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

const saveClosedGroups = keys => {
  try {
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(keys))
  } catch (e) {
    // storage unavailable — group state just won't persist
  }
}

class SidebarContent extends Component {
  constructor(props) {
    super(props)
    this.scrollRef = React.createRef()
    this.state = {
      isMobile: window.innerWidth <= MOBILE_BREAKPOINT,
      closedGroups: readClosedGroups(),
    }
    this.handleResize = this.handleResize.bind(this)
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize)
    this.openActiveGroup()
    this.scrollActiveIntoView()
  }

  componentDidUpdate(prevProps) {
    if (this.props.location?.pathname !== prevProps.location?.pathname) this.openActiveGroup()
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize)
  }

  handleResize() {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT
    if (isMobile !== this.state.isMobile) this.setState({ isMobile })
  }

  scrollActiveIntoView() {
    const active = this.scrollRef.current?.getScrollElement?.()?.querySelector('[aria-current="page"]')
    if (active?.scrollIntoView) active.scrollIntoView({ block: "nearest" })
  }

  // The section holding the current page is re-opened whenever you land on it
  openActiveGroup() {
    const activeKey = findActiveGroupKey(this.props.location?.pathname || "")
    if (activeKey && this.state.closedGroups.includes(activeKey)) {
      const next = this.state.closedGroups.filter(k => k !== activeKey)
      saveClosedGroups(next)
      this.setState({ closedGroups: next })
    }
  }

  toggleGroup = key => {
    const { closedGroups } = this.state
    const next = closedGroups.includes(key) ? closedGroups.filter(k => k !== key) : [...closedGroups, key]
    saveClosedGroups(next)
    this.setState({ closedGroups: next })
  }

  renderItem(item, currentPath, isRail) {
    const { t } = this.props
    const label = t(item.label)
    const isActive = isMenuItemActive(item.path, currentPath)
    const id = `vp-nav-${item.path.slice(1)}`
    const Icon = item.icon

    return (
      <li key={item.path}>
        <Link
          to={item.path}
          id={id}
          className={`vp-sidebar__link${isActive ? " is-active" : ""}`}
          aria-current={isActive ? "page" : undefined}
          aria-label={isRail ? label : undefined}
        >
          <Icon className="vp-sidebar__icon" size={18} strokeWidth={1.9} aria-hidden="true" />
          <span className="vp-sidebar__label">{label}</span>
        </Link>
        {isRail && (
          <UncontrolledTooltip placement="right" target={id} fade={false}>
            {label}
          </UncontrolledTooltip>
        )}
      </li>
    )
  }

  renderGroup(group, currentPath, isRail) {
    const { t } = this.props
    const isOpen = isRail || !this.state.closedGroups.includes(group.key)
    const listId = `vp-nav-group-${group.key}`

    return (
      <div key={group.key} className="vp-sidebar__group">
        {isRail ? (
          <div className="vp-sidebar__divider" role="separator" />
        ) : (
          <button
            type="button"
            className="vp-sidebar__group-toggle"
            aria-expanded={isOpen}
            aria-controls={listId}
            onClick={() => this.toggleGroup(group.key)}
          >
            <span>{t(group.title)}</span>
            <ChevronDown className="vp-sidebar__chevron" size={14} strokeWidth={2.2} aria-hidden="true" />
          </button>
        )}
        <ul id={listId} className="vp-sidebar__list" hidden={!isOpen}>
          {group.items.map(item => this.renderItem(item, currentPath, isRail))}
        </ul>
      </div>
    )
  }

  render() {
    const { location, leftSideBarType, onToggle } = this.props
    const { isMobile } = this.state
    const currentPath = location?.pathname || ""
    const isRail = !isMobile && leftSideBarType === "condensed"

    return (
      <nav id="app-sidebar" className={`vp-sidebar${isRail ? " vp-sidebar--rail" : ""}`} aria-label="Main navigation">
        {isMobile && (
          <div className="vp-sidebar__mobile-head">
            <Link to="/dashboard" className="vp-sidebar__brand">
              <span className="brand-icon-box">
                <img src={logo} alt="" height="18" />
              </span>
              <span className="vp-sidebar__brand-text">
                <span className="vp-sidebar__brand-name">Veeper</span>
                <span className="vp-sidebar__brand-sub">Admin Panel</span>
              </span>
            </Link>
            <button
              type="button"
              id="vp-sidebar-close"
              className="vp-sidebar__icon-btn"
              onClick={onToggle}
              aria-label="Close navigation menu"
            >
              <X size={18} strokeWidth={2.2} />
            </button>
          </div>
        )}

        <SimpleBar className="vp-sidebar__scroll" ref={this.scrollRef}>
          <div className="vp-sidebar__groups">
            {MENU_GROUPS.map(group => this.renderGroup(group, currentPath, isRail))}
          </div>
        </SimpleBar>

        {!isMobile && (
          <div className="vp-sidebar__footer">
            <button
              type="button"
              id="vp-sidebar-collapse"
              className="vp-sidebar__collapse-btn"
              onClick={onToggle}
              aria-label={isRail ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isRail ? (
                <PanelLeftOpen size={18} strokeWidth={1.9} aria-hidden="true" />
              ) : (
                <PanelLeftClose size={18} strokeWidth={1.9} aria-hidden="true" />
              )}
              <span className="vp-sidebar__label">Collapse sidebar</span>
            </button>
            {isRail && (
              <UncontrolledTooltip placement="right" target="vp-sidebar-collapse" fade={false}>
                Expand sidebar
              </UncontrolledTooltip>
            )}
          </div>
        )}
      </nav>
    )
  }
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.func,
  leftSideBarType: PropTypes.string,
  onToggle: PropTypes.func,
}

const mapStateToProps = state => ({
  leftSideBarType: state.Layout.leftSideBarType,
})

export default connect(mapStateToProps)(withRouter(withTranslation()(SidebarContent)))
